import React, { useState, useEffect } from 'react';
import {
    Stack, Typography, TextField, Box, Select, MenuItem, Button,
    IconButton, Tooltip, FormControlLabel, Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTBContext } from '../../Contexts/useTBStates';
import type { SeriesPoints, SeriesPointsRange, SeriesDivisionPoints, SeriesDivisionMode } from '@moment-golf/types';

interface SeriesPointsEditorProps {
    divisionOpts: string[];
}

function syncDivisionPoints(
    existing: SeriesDivisionPoints[],
    divisionOpts: string[]
): SeriesDivisionPoints[] {
    return divisionOpts.map(div => {
        const found = existing.find(d => d.division === div);
        return found ?? { division: div, mode: 'global-scaled' as SeriesDivisionMode, scale: 1, customPoints: [] };
    });
}

function getNextStartPlace(ranges: SeriesPointsRange[]): number {
    if (ranges.length === 0) return 1;
    const last = ranges[ranges.length - 1];
    return (last.endPlace ?? last.startPlace) + 1;
}

function getScaledPoints(globalPoints: number, scale: number): number {
    return Math.round(globalPoints * scale);
}

const COL_WIDTH_SMALL = 72;
const COL_WIDTH_GLOBAL = 80;
const COL_WIDTH_DIV = 128;
const COL_WIDTH_ACTIONS = 36;
const CELL_H = 40;
// Header accommodates: label + select (28px) + optional scale input (32px) + gaps
const HEADER_H_WITH_SCALE = 108;
const HEADER_H_NO_SCALE = 68;

export const SeriesPointsEditor = ({ divisionOpts }: SeriesPointsEditorProps) => {
    const { viewGroup, setViewGroup, editMode, factor, appTheme } = useTBContext();

    const [localRanges, setLocalRanges] = useState<SeriesPointsRange[]>(
        viewGroup?.seriesPoints?.ranges ?? []
    );
    const [localDivPoints, setLocalDivPoints] = useState<SeriesDivisionPoints[]>(() =>
        syncDivisionPoints(viewGroup?.seriesPoints?.divisionPoints ?? [], divisionOpts)
    );

    const allowMultiplier = viewGroup?.seriesPoints?.allowMultiplier ?? false;

    const commit = (ranges: SeriesPointsRange[], divPoints: SeriesDivisionPoints[], allowMult: boolean = allowMultiplier) => {
        if (!viewGroup) return;
        const seriesPoints: SeriesPoints = { ranges, divisionPoints: divPoints, allowMultiplier: allowMult };
        setViewGroup({ ...viewGroup, seriesPoints });
    };

    useEffect(() => {
        setLocalRanges(viewGroup?.seriesPoints?.ranges ?? []);
        setLocalDivPoints(syncDivisionPoints(viewGroup?.seriesPoints?.divisionPoints ?? [], divisionOpts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewGroup?._id]);

    useEffect(() => {
        setLocalDivPoints(prev => {
            const next = syncDivisionPoints(prev, divisionOpts);
            commit(localRanges, next);
            return next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [divisionOpts.join(',')]);

    const handleAddRange = () => {
        const nextRanges = [...localRanges, { startPlace: getNextStartPlace(localRanges), endPlace: null, points: 0 }];
        const nextDivPoints = localDivPoints.map(d => ({ ...d, customPoints: [...d.customPoints, 0] }));
        setLocalRanges(nextRanges);
        setLocalDivPoints(nextDivPoints);
        commit(nextRanges, nextDivPoints);
    };

    const handleDeleteRange = (idx: number) => {
        const nextRanges = localRanges.filter((_, i) => i !== idx);
        const nextDivPoints = localDivPoints.map(d => ({ ...d, customPoints: d.customPoints.filter((_, i) => i !== idx) }));
        setLocalRanges(nextRanges);
        setLocalDivPoints(nextDivPoints);
        commit(nextRanges, nextDivPoints);
    };

    const updateRange = (idx: number, field: keyof SeriesPointsRange, rawValue: string) => {
        const value = rawValue === '' ? null : Number(rawValue);
        const nextRanges = localRanges.map((r, i) => i === idx ? { ...r, [field]: value } : r);
        setLocalRanges(nextRanges);
        commit(nextRanges, localDivPoints);
    };

    const updateDivMode = (divIdx: number, mode: SeriesDivisionMode) => {
        const nextDivPoints = localDivPoints.map((d, i) => i === divIdx ? { ...d, mode } : d);
        setLocalDivPoints(nextDivPoints);
        commit(localRanges, nextDivPoints);
    };

    const updateDivScale = (divIdx: number, rawValue: string) => {
        const scale = rawValue === '' ? 1 : Math.max(0.01, Number(rawValue));
        const nextDivPoints = localDivPoints.map((d, i) => i === divIdx ? { ...d, scale } : d);
        setLocalDivPoints(nextDivPoints);
        commit(localRanges, nextDivPoints);
    };

    const updateDivCustomPoint = (divIdx: number, rangeIdx: number, rawValue: string) => {
        const value = rawValue === '' ? 0 : Number(rawValue);
        const nextDivPoints = localDivPoints.map((d, i) => {
            if (i !== divIdx) return d;
            const cp = [...d.customPoints];
            cp[rangeIdx] = value;
            return { ...d, customPoints: cp };
        });
        setLocalDivPoints(nextDivPoints);
        commit(localRanges, nextDivPoints);
    };

    if (!viewGroup) return null;

    const f = factor ?? 1;
    const smallW = COL_WIDTH_SMALL * f;
    const globalW = COL_WIDTH_GLOBAL * f;
    const divW = COL_WIDTH_DIV * f;
    const actionsW = COL_WIDTH_ACTIONS * f;
    const cellH = CELL_H * f;

    // Header height is driven by the tallest possible division header (with scale input).
    // If any division is in global-scaled mode, all headers use the taller height so rows align.
    const anyScaled = localDivPoints.some(d => d.mode === 'global-scaled');
    const headerH = divisionOpts.length > 0
        ? (anyScaled ? HEADER_H_WITH_SCALE : HEADER_H_NO_SCALE) * f
        : 32 * f;

    const inputSx = {
        '& .MuiInputBase-input': { fontSize: 13 * f, padding: '4px 8px' },
        '& .MuiOutlinedInput-root': { height: 32 * f },
    };

    const colLabel = (text: string) => (
        <Typography variant='caption' sx={{ fontWeight: 600, color: appTheme.palette.text.secondary, lineHeight: 1.2 }}>
            {text}
        </Typography>
    );

    // Shared cell box styles
    const headerCell = (width: number, extra?: object) => ({
        width,
        height: headerH,
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'flex-end',
        pb: 0.75,
        gap: 1,
        flexShrink: 0,
        ...extra,
    });

    const dataCell = (width: number, extra?: object) => ({
        width,
        height: cellH,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...extra,
    });

    return (
        <Stack direction='column' spacing={2} sx={{ height: '100%'}}>

            {/* Title — always visible, never scrolls */}
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ borderBottom: `1px dashed ${appTheme.palette.divider}`, pb: 0.5, flexShrink: 0 }}>
                <Typography variant='subtitle1' color='primary' sx={{ fontWeight: 600 }}>
                    Points by Place
                </Typography>
                <FormControlLabel
                    label={<Typography variant='body2'>Allow event multipliers</Typography>}
                    labelPlacement='start'
                    control={
                        <Switch
                            size='small'
                            checked={allowMultiplier}
                            disabled={!editMode}
                            onChange={(e) => commit(localRanges, localDivPoints, e.target.checked)}
                        />
                    }
                    sx={{ mr: 0 }}
                />
            </Stack>

            {localRanges.length === 0 && (
                <Typography variant='body2' color='text.secondary' sx={{ flexShrink: 0 }}>
                    No point ranges configured. {editMode ? 'Use "Add Range" below to get started.' : 'Enable editing to add ranges.'}
                </Typography>
            )}

            {/* Table — header is sticky, data rows scroll; both scroll horizontally together */}
            <Stack direction={'column'} spacing={0} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* Column headers — sticky within the scroll container */}
                <Stack
                    direction='row'
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        bgcolor: appTheme?.palette?.sepia?.background ?? 'sepia.background',
                        borderBottom: `1px solid ${appTheme.palette.divider}`,
                        minWidth: 'max-content',
                    }}
                >
                    <Box sx={headerCell(smallW, { mr: 1 })}>
                        {colLabel('Start')}
                    </Box>
                    <Box sx={headerCell(smallW, { mr: 1 })}>
                        {colLabel('End (opt)')}
                    </Box>
                    <Box sx={headerCell(globalW, { mr: divisionOpts.length > 0 ? 3 : 1 })}>
                        {colLabel('Global Pts')}
                    </Box>

                    {localDivPoints.map((divPts, divIdx) => (
                        <Box
                            key={divPts.division}
                            sx={headerCell(divW, {
                                mr: 2,
                                pl: 2,
                                borderLeft: `1px dashed ${appTheme.palette.divider}`,
                            })}
                        >
                            <Tooltip title={divPts.division}>
                                <Typography
                                    variant='caption'
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 12 * f,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: divW - 16,
                                    }}
                                >
                                    {divPts.division}
                                </Typography>
                            </Tooltip>

                            <Select
                                size='small'
                                disabled={!editMode}
                                value={divPts.mode}
                                onChange={e => updateDivMode(divIdx, e.target.value as SeriesDivisionMode)}
                                sx={{ fontSize: 12 * f, height: 28 * f, width: divW - 16, mb: 0.5 }}
                            >
                                <MenuItem value='global-scaled'><Typography variant='caption'>Global</Typography></MenuItem>
                                <MenuItem value='custom'><Typography variant='caption'>Custom</Typography></MenuItem>
                            </Select>

                            {/* Placeholder keeps height consistent when not in global-scaled mode */}
                            {divPts.mode === 'global-scaled' ? (
                                <TextField
                                    type='number'
                                    size='small'
                                    disabled={!editMode}
                                    value={divPts.scale}
                                    label='Scale'
                                    onChange={e => updateDivScale(divIdx, e.target.value)}
                                    inputProps={{ min: 0.01, step: 0.05 }}
                                    sx={{ ...inputSx, width: divW - 16 }}
                                />
                            ) : anyScaled ? (
                                <Box sx={{ height: 32 * f }} />
                            ) : null}
                        </Box>
                    ))}

                    {/* Actions column header spacer */}
                    {editMode && <Box sx={headerCell(actionsW)} />}
                </Stack>

                {/* Data rows */}
                <Stack direction={'column'} spacing={0} sx={{ minWidth: 'max-content', flex: 1, overflow: 'auto' }}>
                    {localRanges.map((range, idx) => (
                        <Stack
                            key={idx}
                            direction='row'
                            sx={{ borderBottom: `1px dashed ${appTheme.palette.divider}` }}
                        >
                            <Box sx={dataCell(smallW, { mr: 1 })}>
                                <TextField
                                    type='number'
                                    size='small'
                                    disabled={!editMode}
                                    value={range.startPlace ?? ''}
                                    onChange={e => updateRange(idx, 'startPlace', e.target.value)}
                                    inputProps={{ min: 1 }}
                                    sx={{ ...inputSx, width: smallW - 8 }}
                                />
                            </Box>

                            <Box sx={dataCell(smallW, { mr: 1 })}>
                                <TextField
                                    type='number'
                                    size='small'
                                    disabled={!editMode}
                                    value={range.endPlace ?? ''}
                                    placeholder='—'
                                    onChange={e => updateRange(idx, 'endPlace', e.target.value)}
                                    inputProps={{ min: range.startPlace }}
                                    sx={{ ...inputSx, width: smallW - 8 }}
                                />
                            </Box>

                            <Box sx={dataCell(globalW, { mr: divisionOpts.length > 0 ? 3 : 1 })}>
                                <TextField
                                    type='number'
                                    size='small'
                                    disabled={!editMode}
                                    value={range.points ?? ''}
                                    onChange={e => updateRange(idx, 'points', e.target.value)}
                                    inputProps={{ min: 0 }}
                                    sx={{ ...inputSx, width: globalW - 8 }}
                                />
                            </Box>

                            {localDivPoints.map((divPts, divIdx) => (
                                <Box
                                    key={divPts.division}
                                    sx={dataCell(divW, {
                                        mr: 2,
                                        pl: 2,
                                        borderLeft: `1px dashed ${appTheme.palette.divider}`,
                                    })}
                                >
                                    {divPts.mode === 'global-scaled' ? (
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                width: divW - 16,
                                                textAlign: 'center',
                                                color: appTheme.palette.text.secondary,
                                                fontStyle: 'italic',
                                                fontSize: 13 * f,
                                            }}
                                        >
                                            {getScaledPoints(range.points, divPts.scale)}
                                        </Typography>
                                    ) : (
                                        <TextField
                                            type='number'
                                            size='small'
                                            disabled={!editMode}
                                            value={divPts.customPoints[idx] ?? 0}
                                            onChange={e => updateDivCustomPoint(divIdx, idx, e.target.value)}
                                            inputProps={{ min: 0 }}
                                            sx={{ ...inputSx, width: divW - 16 }}
                                        />
                                    )}
                                </Box>
                            ))}

                            {editMode && (
                                <Box sx={dataCell(actionsW)}>
                                    <IconButton
                                        size='small'
                                        color='error'
                                        onClick={() => handleDeleteRange(idx)}
                                        sx={{ padding: '2px' }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 16 * f }} />
                                    </IconButton>
                                </Box>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Stack>

            {/* Add Range — always visible, never scrolls */}
            {editMode && (
                <Button
                    variant='outlined'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={handleAddRange}
                    sx={{ fontSize: 12 * f, textTransform: 'none', alignSelf: 'flex-start', flexShrink: 0 }}
                >
                    Add Range
                </Button>
            )}

            {divisionOpts.length === 0 && (
                <Typography variant='caption' color='text.disabled' sx={{ flexShrink: 0 }}>
                    Division columns appear automatically when linked events have divisions configured.
                </Typography>
            )}
        </Stack>
    );
};
